import { useEffect, useRef, useState } from "react"
import * as amplitude from "@amplitude/analytics-react-native"
import { summerFunnelService } from "../services/summerFunnelService"

// Un seul retry court : l'échec typique est un blip réseau mobile. Au-delà, on
// trace l'échec (Amplitude) — un fetch raté était jusqu'ici invisible alors
// qu'il dégrade l'affichage du prix en plein tarif (cas prod du 07/07).
const RETRY_DELAY_MS = 1500

/**
 * Options de date de démarrage pour la sélection courante (créneaux + mois +
 * engagement) — source UNIQUE des dates ET des montants proratisés (le front ne
 * recalcule jamais la règle, leçon P-2234). Re-fetch quand la sélection
 * effective change ; le choix courant (`startDate`) est renvoyé au backend, qui
 * l'échoie tant qu'il reste une option valide (sinon il retombe sur la plus
 * proche). Échec (après retry) → `options` reste vide, `failed` passe à true et
 * les écrans gardent leur fallback générique (pas de picker, copy neutre).
 *
 * Retourne { options, selectedOption, monthlyPriceCents, failed }. La sélection
 * elle-même vit dans le contexte funnel (state.startDate) — le hook ne fait que
 * résoudre l'option correspondante.
 */
export const useSummerStartOptions = ({
    stateId,
    slotIds,
    months,
    withCommitment,
    startDate,
}) => {
    const [payload, setPayload] = useState(null)
    const [failed, setFailed] = useState(false)

    // Le choix courant est lu à la volée au (re)fetch, sans être une dépendance
    // de l'effet : sélectionner une option ne doit PAS re-fetcher (les montants
    // de chaque option sont déjà là) — seul un changement de créneaux/mois/
    // engagement invalide la liste.
    const startDateRef = useRef(startDate)
    startDateRef.current = startDate

    useEffect(() => {
        // Sélection incomplète (pas de créneau OU pas de mois — possible depuis
        // la dé-présélection du configurateur) → pas d'options calculables ; le
        // backend refuse d'ailleurs un `months` vide (min:1). On purge les
        // options du fetch précédent (elles dataient d'une sélection qui n'existe
        // plus) et on garde le fallback générique sans tirer une requête vouée
        // au 422.
        if (!stateId || !slotIds?.length || !months?.length) {
            setPayload(null)
            return
        }
        let cancelled = false
        let retryTimer = null
        setFailed(false)

        const fetchOptions = (attempt) => {
            summerFunnelService
                .getStartOptions(
                    { stateId },
                    {
                        slotIds,
                        months,
                        withCommitment: !!withCommitment,
                        startDate: startDateRef.current,
                    }
                )
                .then((res) => {
                    if (cancelled) return
                    setPayload(res?.data?.data ?? res?.data ?? null)
                    setFailed(false)
                })
                .catch((err) => {
                    if (cancelled) return
                    if (attempt === 0) {
                        retryTimer = setTimeout(() => {
                            if (!cancelled) fetchOptions(1)
                        }, RETRY_DELAY_MS)
                        return
                    }
                    // Échec définitif : fallback générique conservé, mais tracé —
                    // c'est lui qui remplace le prorata par le plein tarif.
                    setFailed(true)
                    amplitude.track("summer_funnel_start_options_failed", {
                        funnel_state_id: stateId,
                        error_status: err?.response?.status ?? "network",
                    })
                })
        }

        fetchOptions(0)

        return () => {
            cancelled = true
            if (retryTimer) clearTimeout(retryTimer)
        }
        // Sérialisé en chaîne pour des deps stables (pas d'identité de tableau).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateId, (slotIds || []).join(","), (months || []).join(","), !!withCommitment])

    const options = payload?.options || []
    const selectedOption =
        options.find((o) => o.start_date === startDate) ||
        options.find((o) => o.start_date === payload?.selected_start_date) ||
        options[0] ||
        null

    return {
        options,
        selectedOption,
        monthlyPriceCents: payload?.monthly_price_cents ?? null,
        failed,
    }
}
